import { Box, Text, Menu, MenuButton, MenuList, MenuItem, IconButton, Divider, Portal } from '@chakra-ui/react'
import type { HelpdeskRequest } from '@/types/helpdesk'
import { StatusPill } from '../Cell/StatusPill'
import { useNavigate } from 'react-router-dom'
import { MoreVertical, Trash2, RotateCcw } from 'lucide-react'

type Row = HelpdeskRequest & {
  deleted_at?: string | null
  acknowledgedAt?: string
  currentStatusLabel?: string
}

function formatAgeingFromAcknowledge(requestedAt: string, acknowledgedAt?: string) {
  const start = new Date(acknowledgedAt ?? requestedAt).getTime()
  const diff = Date.now() - start
  const mins = Math.floor(diff / 60000)
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
}

// ✅ + kebab col
const COLS =
  '160px minmax(190px, 0.9fr) 155px minmax(180px, 1.3fr) minmax(220px, 1fr) 110px 180px 48px'

const MIN_TABLE_W = '1150px'

type Props = {
  data?: Row[]
  height?: string | number
  onTrash?: (id: string) => void
  onRestore?: (id: string) => void
  onDeletePermanent?: (id: string) => void
}

export function AcknowledgedQueue({
  data = [],
  height = '100%',
  onTrash,
  onRestore,
  onDeletePermanent,
}: Props) {
  const navigate = useNavigate()

  const goCreate = (requestId: string) => {
    navigate(`/tickets/create?request_id=${encodeURIComponent(requestId)}`)
  }

  // ✅ Important: ONLY stop propagation (do NOT preventDefault)
  const stopRowClick = (e: React.SyntheticEvent) => {
    e.stopPropagation()
  }

  return (
    <Box
      bg="white"
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.200"
      overflow="hidden"
      h={height}
      minW={0}
      display="flex"
      flexDirection="column"
    >
      <Box flex="1" minH={0} overflowX="auto" overflowY="hidden" minW={0}>
        <Box minW={MIN_TABLE_W} h="100%">
          <Box maxH="100%" overflowY="auto" sx={{ scrollBehavior: 'smooth' }}>
            {/* Header */}
            <Box
              position="sticky"
              top={0}
              zIndex={2}
              display="grid"
              gridTemplateColumns={COLS}
              px={4}
              py={3}
              bg="white"
              fontSize="sm"
              fontWeight="semibold"
              borderBottom="1px solid"
              borderColor="gray.200"
            >
              <Box>Request ID</Box>
              <Box>Client</Box>
              <Box>Request Type</Box>
              <Box>Subject</Box>
              <Box>Date and Time of Request</Box>
              <Box>Ageing</Box>
              <Box>Current Status</Box>
              <Box />
            </Box>

            {/* Rows */}
            {data.map((r) => {
              const isTrashed = !!r.deleted_at

              return (
                <Box
                  key={r.id}
                  display="grid"
                  gridTemplateColumns={COLS}
                  px={4}
                  py={3}
                  borderBottom="1px solid"
                  borderColor="gray.100"
                  fontSize="sm"
                  alignItems="center"
                  role="button"
                  tabIndex={0}
                  cursor="pointer"
                  _hover={{ bg: 'gray.50' }}
                  _focusVisible={{
                    outline: '2px solid',
                    outlineColor: 'blue.500',
                    outlineOffset: '2px',
                  }}
                  onClick={() => goCreate(r.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') goCreate(r.id)
                    if (e.key === ' ' || e.key === 'Spacebar') {
                      e.preventDefault()
                      goCreate(r.id)
                    }
                  }}
                >
                  <Box>{r.id}</Box>

                  <Box minW={0}>
                    <Text fontWeight="semibold" noOfLines={1}>
                      {r.clientName}
                    </Text>
                    <Text fontSize="xs" color="gray.500" noOfLines={1}>
                      {r.clientOrg ?? '-'}
                    </Text>
                  </Box>

                  <Box>{r.requestType}</Box>

                  <Box minW={0}>
                    <Text noOfLines={1}>{r.subject}</Text>
                  </Box>

                  <Box minW={0} whiteSpace="nowrap">
                    {new Date(r.requestedAt).toLocaleString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Box>

                  <Box>{formatAgeingFromAcknowledge(r.requestedAt, r.acknowledgedAt)}</Box>

                  <Box>
                    <StatusPill label={r.currentStatusLabel ?? 'Acknowledged'} variant="status" />
                  </Box>

                  {/* ✅ Actions column (does not trigger row click) */}
                  <Box justifySelf="end" onClick={stopRowClick}>
                    <Menu placement="bottom-end" isLazy>
                      <MenuButton
                        as={IconButton}
                        aria-label="Actions"
                        icon={<MoreVertical size={18} />}
                        variant="ghost"
                        size="sm"
                        onClick={stopRowClick}
                      />

                      {/* ✅ Portal so Menu isn't clipped by overflow containers */}
                      <Portal>
                        <MenuList minW="180px" zIndex={2000} onClick={stopRowClick}>
                          {!isTrashed ? (
                            <MenuItem
                              icon={<Trash2 size={16} />}
                              color="orange.600"
                              onClick={(e) => {
                                e.stopPropagation()
                                onTrash?.(r.id)
                              }}
                            >
                              Trash
                            </MenuItem>
                          ) : (
                            <>
                              <MenuItem
                                icon={<RotateCcw size={16} />}
                                color="green.600"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  onRestore?.(r.id)
                                }}
                              >
                                Restore
                              </MenuItem>

                              <Divider />

                              <MenuItem
                                icon={<Trash2 size={16} />}
                                color="red.600"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  onDeletePermanent?.(r.id)
                                }}
                              >
                                Delete Permanently
                              </MenuItem>
                            </>
                          )}
                        </MenuList>
                      </Portal>
                    </Menu>
                  </Box>
                </Box>
              )
            })}

            {data.length === 0 && (
              <Box px={4} py={8} textAlign="center" color="gray.500">
                No Record Found
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
